import { BaseClient } from '../base-client';
import type {
  CreateHolidayInput,
  ExportHolidaysQuery,
  Holiday,
  ImportHolidaysResult,
  ListHolidaysQuery,
  PatchHolidayInput,
} from './types';

type ListResponse = { data: Holiday[]; meta: { total: number; limit: number; offset: number } };
type One = { data: Holiday };
type ImportResponse = { data: ImportHolidaysResult };

export class HolidaysClient extends BaseClient {
  list(query: ListHolidaysQuery = {}): Promise<ListResponse> {
    return this.request('/api/holidays', { query: normalize(query) });
  }

  /** Batch fetch holidays by exactly the referenced ids — bounded, never
   *  capped. See `BaseClient.findByIdsAt`. */
  findByIds(ids: readonly string[]): Promise<ListResponse> {
    return this.findByIdsAt<Holiday>('/api/holidays/find', ids);
  }

  /**
   * The distinct countries this tenant has holidays for. Drives the list
   * page's country filter, which offers only countries with rows rather than
   * all 249 ISO codes.
   */
  countries(): Promise<{ data: string[] }> {
    return this.request('/api/holidays/countries');
  }

  get(id: string): Promise<One> {
    return this.request(`/api/holidays/${encodeURIComponent(id)}`);
  }

  create(input: CreateHolidayInput): Promise<One> {
    return this.request('/api/holidays', { method: 'POST', body: input });
  }

  patch(id: string, input: PatchHolidayInput): Promise<One> {
    return this.request(`/api/holidays/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    });
  }

  /** Soft delete — sets `active = false`. The row stays for audit and for
   *  historical working-day calculations; use `restore` to undo. */
  remove(id: string): Promise<One> {
    return this.request(`/api/holidays/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      body: {},
    });
  }

  restore(id: string): Promise<One> {
    return this.request(`/api/holidays/${encodeURIComponent(id)}/restore`, {
      method: 'POST',
      body: {},
    });
  }

  /**
   * Download the tenant's calendar as an XLSX blob. `filter` mirrors the
   * list-page filters; pass `{}` to export everything.
   */
  exportXlsx(filter: ExportHolidaysQuery = {}): Promise<{ blob: Blob; filename: string | null }> {
    return this.requestBlob('/api/holidays/export', { query: normalize(filter) });
  }

  /** Download a blank XLSX template with just the import header row. */
  exportTemplate(): Promise<{ blob: Blob; filename: string | null }> {
    return this.requestBlob('/api/holidays/import/template');
  }

  /**
   * Upload an .xlsx or .csv file. Rows are keyed on the natural key
   * (country + date + holiday), so re-importing a file updates rather than
   * duplicating. Rows that fail validation come back in the result with
   * their per-row errors so the caller can render a report.
   *
   * Pass `idempotencyKey` to make the request safely retryable — the server
   * replays the stored response instead of re-applying the import.
   */
  importFile(file: File, opts: { idempotencyKey?: string } = {}): Promise<ImportResponse> {
    const fd = new FormData();
    fd.append('file', file, file.name);
    const headers: Record<string, string> = {};
    if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
    return this.requestMultipart('/api/holidays/import', fd, { headers });
  }
}

function normalize(q: object): Record<string, string | number | boolean | undefined> {
  const out: Record<string, string | number | boolean | undefined> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === '') continue;
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      out[k] = v;
    }
  }
  return out;
}
